<script setup lang="ts">
import Password from '@/components/Password.vue';
import { useToast } from '@/composables/useToast';
import { useI18n } from '@/i18n';
import { createPasswordChangeSchema, type PasswordChangeFormValues } from '@/schemas/passwordChange';
import { useUserStore } from '@/stores/user';
import { Button, Divider, Form, Heading, LayoutGap } from '@dabble/quillui';
import { toTypedSchema } from '@vee-validate/yup';
import { useForm, useIsFormDirty } from 'vee-validate';
import { computed, onMounted, ref } from 'vue';

const { t, te } = useI18n();
const userStore = useUserStore();
const { showToast } = useToast();

function translateFieldError(raw: string | undefined) {
  if (!raw) return '';
  return te(raw) ? t(raw) : raw;
}

const isLoading = ref(false);

const rawPasswordChangeSchema = computed(() => createPasswordChangeSchema((key: string) => t(key)));
const validationSchema = computed(() => toTypedSchema(rawPasswordChangeSchema.value));

const { handleSubmit, defineField, values, resetForm } = useForm<PasswordChangeFormValues>({
  validationSchema,
  initialValues: {
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  },
});

const isValid = computed(() =>
  rawPasswordChangeSchema.value.isValidSync({
    currentPassword: values.currentPassword ?? '',
    newPassword: values.newPassword ?? '',
    confirmPassword: values.confirmPassword ?? '',
  }),
);

const isDirty = useIsFormDirty();

const fieldOpts = {
  validateOnBlur: true,
  validateOnChange: true,
  validateOnModelUpdate: true,
  props: (state: { errors: string[] }) => ({
    error: state.errors.length > 0,
    errorMessage: translateFieldError(state.errors[0]),
  }),
};

const [currentPassword, currentPasswordField] = defineField('currentPassword', fieldOpts);
const [newPassword, newPasswordField] = defineField('newPassword', fieldOpts);
const [confirmPassword, confirmPasswordField] = defineField('confirmPassword', fieldOpts);

onMounted(async () => {
  if (userStore.user.uid) {
    await userStore.fetchUser(userStore.user.uid);
  }
});

const onSubmit = handleSubmit(async (formValues) => {
  isLoading.value = true;
  try {
    await userStore.updatePassword(userStore.user?.email || '', formValues.currentPassword, formValues.newPassword);
    resetForm({
      values: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      },
    });
    showToast('success', t('account_password_updated'));
  } catch (error: any) {
    showToast('danger', error.message);
  } finally {
    isLoading.value = false;
  }
});
</script>

<template>
  <LayoutGap direction="column" align="stretch" size="medium" fill class="password-view">
    <LayoutGap direction="column" align="stretch" size="xsmall" fill>
      <Heading :level="1">{{ t('account_password_title') }}</Heading>
      <Divider variant="secondary" size="small" />
    </LayoutGap>

    <LayoutGap direction="column" align="stretch" size="small" fill class="password-view__content">
      <Form v-model:dirty="isDirty" :valid="isValid" :actionless="true" class="password-view__form" @submit="onSubmit">
        <LayoutGap direction="column" align="stretch" size="small" fill>
          <Password
            id="password-current"
            v-model="currentPassword"
            v-bind="currentPasswordField"
            :label="t('account_password_current')"
            name="current-password"
            :show-requirements="false"
          />
          <Password
            id="password-new"
            v-model="newPassword"
            v-bind="newPasswordField"
            :label="t('account_password_new')"
            name="new-password"
            :show-requirements="true"
          />
          <Password
            id="password-confirm"
            v-model="confirmPassword"
            v-bind="confirmPasswordField"
            :label="t('account_password_confirm')"
            name="confirm-password"
            :show-requirements="false"
          />
          <Button fill-width class="password-view__submit" type="submit" :disabled="isLoading || !isValid || !isDirty">
            {{ isLoading ? t('account_password_updating') : t('account_password_submit') }}
          </Button>
        </LayoutGap>
      </Form>
    </LayoutGap>
  </LayoutGap>
</template>

<style lang="scss" scoped>
.password-view {
  max-width: 465px;

  &__content {
    background: theme('color-bg-app-panel');
  }

  &__form {
    width: 100%;
  }

  &__submit {
    margin-top: space('small');
    max-width: 211px;
  }
}
</style>
